import { useState } from 'react';
import { Link, createFileRoute, useRouter } from '@tanstack/react-router';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { ChevronLeft } from 'lucide-react';
import { JourneyPlanningDetail, PageBody, Spinner, backLinkVariants } from '@wundo/ui';
import { PageHeader } from '@/components/PageHeader.tsx';
import {
  JourneyActionDialogs,
  type JourneyAction,
} from '@/components/journeys/JourneyActionDialogs.tsx';
import { JourneysFeatureNotice } from '@/components/journeys/JourneysFeatureNotice.tsx';
import { ManagerReachabilityBadge } from '@/components/journeys/ManagerReachabilityBadge.tsx';
import {
  JOURNEY_LOCALE,
  JOURNEY_PLANNING_LABELS,
} from '@/components/journeys/journeyLabels.ts';
import type { JourneyPlanningRecord } from '@wundo/shared/journey.ts';
import { readJourney } from '@/lib/journeys.ts';
import { useRecordOrganisation } from '@/lib/recordOrganisation.ts';

export const Route = createFileRoute('/_app/journeys/organisations/$organisationId/$id')({
  staticData: { orgScope: 'record', orgScopeList: '/journeys' },
  component: JourneyDetailRoute,
});

function JourneyDetailRoute() {
  const { organisationId, id } = Route.useParams();
  return <JourneyDetailPage organisationId={organisationId} journeyId={id} />;
}

export interface JourneyDetailPageProps {
  /* From the URL rather than the picker, so a link into another organisation still opens. */
  organisationId: string;
  journeyId: string;
}

/** One journey's planning: per step when it is due, where it stands, and on which day it is planned. */
export function JourneyDetailPage({ organisationId, journeyId }: JourneyDetailPageProps) {
  const router = useRouter();
  useRecordOrganisation(organisationId);

  const journey = useQuery({
    queryKey: ['journey', organisationId, journeyId],
    queryFn: () => readJourney(organisationId, journeyId),
  });

  const [action, setAction] = useState<JourneyAction | null>(null);
  const record = journey.data;

  return (
    <>
      <Link to='/journeys' className={backLinkVariants({ className: 'mb-2.5' })}>
        <ChevronLeft aria-hidden='true' />
        Journeys
      </Link>
      <PageHeader title={record?.employee_name ?? 'Journey'} />
      <PageBody flush className='mt-7 flex flex-col gap-5'>
        <JourneysFeatureNotice organisationId={organisationId} />

        <JourneyBody journey={journey} onAction={setAction} />
      </PageBody>

      <JourneyActionDialogs
        organisationId={organisationId}
        action={action}
        onClose={() => setAction(null)}
        onDeleted={() => void router.navigate({ to: '/journeys' })}
      />
    </>
  );
}

function JourneyBody({
  journey,
  onAction,
}: {
  journey: UseQueryResult<JourneyPlanningRecord>;
  onAction: (action: JourneyAction) => void;
}) {
  const record = journey.data;
  if (journey.isError) {
    return (
      <p role='alert' className='text-sm text-danger'>
        This journey could not be loaded
      </p>
    );
  }
  if (record === undefined) {
    return <Spinner />;
  }
  return (
    <JourneyPlanningDetail
      journey={record}
      labels={JOURNEY_PLANNING_LABELS}
      locale={JOURNEY_LOCALE}
      onStop={() => onAction({ kind: 'stop', journey: record })}
      onDelete={() => onAction({ kind: 'delete', journey: record })}
      reachability={
        <ManagerReachabilityBadge reachability={record.manager_reachability} />
      }
    />
  );
}
